import { ReactNode, useRef } from 'react'
import cn from 'classnames'
import {
   FlowIcon,
   TFlowIcon,
} from '@forwardflow/features/campaign-flow/components/ui/FlowIcon'
import {
   splitVariables,
   VARIABLES,
} from '@forwardflow/features/campaign-flow/utils/text'

/* Small building blocks in the dashboard palette. Layout is Tailwind; colours are the dashboard tokens. */

export const Pill = ({
   tone = 'neutral',
   children,
   className,
   testId,
}: {
   tone?: 'neutral' | 'good' | 'warn' | 'accent' | 'live'
   children: ReactNode
   className?: string
   testId?: string
}) => (
   <span
      data-testid={testId}
      className={cn(
         'inline-flex items-center gap-1.5 h-6 px-2.5 rounded-full text-xs font-semibold whitespace-nowrap',
         tone === 'neutral' && 'bg-white/[0.08] text-[#9ca3af]',
         (tone === 'good' || tone === 'live') &&
            'bg-[#00d857]/[0.14] text-[#00d857]',
         tone === 'warn' && 'bg-amber-400/[0.14] text-amber-400',
         tone === 'accent' && 'bg-[#9061DA]/[0.22] text-[#DAC2FF]',
         className
      )}
   >
      {children}
   </span>
)

export const Disc = ({
   icon,
   size = 'md',
   tone = 'accent',
}: {
   icon: TFlowIcon
   size?: 'sm' | 'md' | 'lg'
   tone?: 'accent' | 'good' | 'neutral'
}) => (
   <div
      className={cn(
         'rounded-full flex items-center justify-center shrink-0',
         size === 'lg' && 'w-[72px] h-[72px]',
         size === 'md' && 'w-9 h-9',
         size === 'sm' && 'w-7 h-7',
         tone === 'accent' && 'bg-[#9061DA]/20 text-[#DAC2FF]',
         tone === 'good' && 'bg-[#00d857]/[0.14] text-[#00d857]',
         tone === 'neutral' && 'bg-white/[0.06] text-white/50'
      )}
   >
      <FlowIcon
         name={icon}
         size={size === 'lg' ? 30 : size === 'md' ? 15 : 13}
      />
   </div>
)

export const Card = ({
   children,
   className,
   testId,
}: {
   children: ReactNode
   className?: string
   testId?: string
}) => (
   <div
      data-testid={testId}
      className={cn(
         'rounded-2xl bg-white/[0.05] border border-white/[0.06] overflow-hidden',
         className
      )}
   >
      {children}
   </div>
)

export const SmallButton = ({
   icon,
   children,
   onClick,
   variant = 'soft',
   disabled,
   testId,
   className,
}: {
   icon?: TFlowIcon
   children: ReactNode
   onClick?: () => void
   variant?: 'soft' | 'primary' | 'outline' | 'ghost'
   disabled?: boolean
   testId?: string
   className?: string
}) => (
   <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      disabled={disabled}
      className={cn(
         'inline-flex items-center justify-center gap-1.5 h-8 px-3 rounded-full text-xs font-semibold whitespace-nowrap transition-colors disabled:opacity-40 disabled:cursor-not-allowed',
         variant === 'soft' && 'bg-white/10 text-white hover:bg-white/[0.16]',
         variant === 'primary' && 'bg-[#9061DA] text-white hover:bg-[#7044B4]',
         variant === 'outline' &&
            'border border-[#9061DA]/50 bg-[#9061DA]/[0.18] text-white hover:bg-[#9061DA]/30',
         variant === 'ghost' && 'text-[#DAC2FF] hover:text-white',
         className
      )}
   >
      {icon && <FlowIcon name={icon} size={13} />}
      {children}
   </button>
)

export const Toggle = ({
   on,
   onChange,
   label,
   testId,
}: {
   on: boolean
   onChange: (on: boolean) => void
   label?: string
   testId?: string
}) => (
   <button
      type="button"
      role="switch"
      aria-checked={on}
      data-testid={testId}
      onClick={() => onChange(!on)}
      className="inline-flex items-center gap-2 text-xs text-[#9ca3af]"
   >
      <span
         className={cn(
            'relative w-[30px] h-[18px] rounded-full transition-colors shrink-0',
            on ? 'bg-[#9061DA]' : 'bg-white/15'
         )}
      >
         <i
            className={cn(
               'absolute top-[2px] w-[14px] h-[14px] rounded-full bg-white transition-all',
               on ? 'left-[14px]' : 'left-[2px]'
            )}
         />
      </span>
      {label}
   </button>
)

export const Radio = ({ on }: { on: boolean }) => (
   <span
      className={cn(
         'w-[18px] h-[18px] rounded-full border-2 shrink-0 flex items-center justify-center',
         on ? 'border-[#9061DA]' : 'border-white/35'
      )}
   >
      {on && <i className="w-2 h-2 rounded-full bg-[#9061DA]" />}
   </span>
)

export const Checkbox = ({ on }: { on: boolean }) => (
   <span
      className={cn(
         'w-[18px] h-[18px] rounded-[5px] border-[1.5px] shrink-0 flex items-center justify-center',
         on ? 'bg-[#9061DA] border-[#9061DA] text-white' : 'border-white/35'
      )}
   >
      {on && <FlowIcon name="check" size={12} strokeWidth={2.4} />}
   </span>
)

/** Text with `{variable}` runs shown as chips. */
export const VarText = ({
   text,
   className,
}: {
   text: string
   className?: string
}) => (
   <span className={className}>
      {splitVariables(text).map((run, i) =>
         run.variable ? (
            <span key={i} className="cf-var">
               {run.text}
            </span>
         ) : (
            <span key={i}>{run.text}</span>
         )
      )}
   </span>
)

/**
 * The editable block used for script sections, texts and voicemails: reads as
 * text with variable chips, becomes a textarea on Edit, with "+ variable"
 * chips that insert at the cursor. Changes are live (no confirm step).
 */
export const EditableText = ({
   label,
   meta,
   text,
   editing,
   onEdit,
   onChange,
   onDone,
   onRemove,
   testId,
   muted,
}: {
   label: ReactNode
   meta?: ReactNode
   text: string
   editing: boolean
   onEdit: () => void
   onChange: (text: string) => void
   onDone: () => void
   onRemove?: () => void
   testId?: string
   muted?: boolean
}) => {
   const ref = useRef<HTMLTextAreaElement | null>(null)
   const insert = (label: string) => {
      const ta = ref.current
      const token = `{${label}}`
      if (!ta) return onChange(text + token)
      const start = ta.selectionStart ?? text.length
      const end = ta.selectionEnd ?? text.length
      onChange(text.slice(0, start) + token + text.slice(end))
      requestAnimationFrame(() => {
         ta.focus()
         ta.setSelectionRange(start + token.length, start + token.length)
      })
   }
   return (
      <div
         data-testid={testId}
         className={cn(
            'rounded-xl border px-3.5 py-2.5 transition-colors',
            editing
               ? 'border-[#9061DA] bg-[#9061DA]/10'
               : 'border-white/[0.06] bg-white/[0.04] hover:border-white/15'
         )}
      >
         <div className="flex items-center gap-2 text-[11px] tracking-[.06em] uppercase text-[#9ca3af] font-semibold mb-1.5">
            <span className="truncate">{label}</span>
            {meta && (
               <span className="normal-case tracking-normal font-medium text-white/40 truncate">
                  · {meta}
               </span>
            )}
            <span className="ml-auto flex items-center gap-1 normal-case tracking-normal">
               {editing ? (
                  <button
                     type="button"
                     onClick={onDone}
                     className="text-xs font-semibold text-[#DAC2FF] hover:text-white"
                     data-testid={testId ? `${testId}-done` : undefined}
                  >
                     Done
                  </button>
               ) : (
                  <button
                     type="button"
                     onClick={onEdit}
                     aria-label="Edit"
                     className="w-7 h-7 rounded-full flex items-center justify-center text-white/45 hover:text-white hover:bg-white/10"
                     data-testid={testId ? `${testId}-edit` : undefined}
                  >
                     <FlowIcon name="pen" size={13} />
                  </button>
               )}
               {onRemove && !editing && (
                  <button
                     type="button"
                     onClick={onRemove}
                     aria-label="Remove"
                     className="w-7 h-7 rounded-full flex items-center justify-center text-white/35 hover:text-white hover:bg-white/10"
                  >
                     <FlowIcon name="x" size={13} />
                  </button>
               )}
            </span>
         </div>
         {editing ? (
            <>
               <textarea
                  ref={ref}
                  autoFocus
                  value={text}
                  onChange={(e) => onChange(e.target.value)}
                  onKeyDown={(e) => e.key === 'Escape' && onDone()}
                  rows={3}
                  className="w-full bg-transparent text-sm leading-5 text-white outline-none resize-none"
                  data-testid={testId ? `${testId}-input` : undefined}
               />
               <div className="flex flex-wrap gap-1.5 mt-1.5">
                  {VARIABLES.map((v) => (
                     <button
                        key={v.token}
                        type="button"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => insert(v.label)}
                        className="text-[11px] px-2 py-0.5 rounded-md border border-dashed border-[#DAC2FF]/50 text-[#DAC2FF] font-semibold hover:bg-[#9061DA]/20"
                     >
                        + {v.label}
                     </button>
                  ))}
               </div>
            </>
         ) : (
            <button
               type="button"
               onClick={onEdit}
               className={cn(
                  'block w-full text-left text-sm leading-5',
                  muted ? 'text-[#9ca3af]' : 'text-white/90'
               )}
            >
               <VarText text={text} />
            </button>
         )}
      </div>
   )
}

export const AddRow = ({
   label,
   hint,
   onClick,
   testId,
}: {
   label: string
   hint?: string
   onClick: () => void
   testId?: string
}) => (
   <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className="flex items-center gap-3 h-12 px-4 rounded-xl border border-dashed border-white/[0.18] text-[#DAC2FF] font-semibold text-sm hover:bg-white/[0.04] text-left"
   >
      <span className="w-7 h-7 rounded-full bg-[#9061DA]/20 flex items-center justify-center">
         <FlowIcon name="plus" size={14} />
      </span>
      {label}
      {hint && (
         <span className="text-xs font-normal text-[#818181] truncate">
            {hint}
         </span>
      )}
   </button>
)

export const Spinner = ({ size = 16 }: { size?: number }) => (
   <span
      className="cf-spin inline-block rounded-full border-2 border-white/25 border-t-white"
      style={{ width: size, height: size }}
   />
)
